// Pure helpers for the admin "Void / Remove order" feature (migration 0027).
// No React, no Supabase, no localStorage here so they can be unit-tested.

// Reduce an append-only event log to { [invoice_ref]: latestEvent }.
// Latest = greatest created_at, ties broken by greatest numeric id.
export function latestRemovalByInvoice(events) {
  const map = {};
  (Array.isArray(events) ? events : []).forEach(ev => {
    if (!ev || !ev.invoice_ref) return;
    const cur = map[ev.invoice_ref];
    if (!cur) { map[ev.invoice_ref] = ev; return; }
    const a = new Date(ev.created_at || 0).getTime();
    const b = new Date(cur.created_at || 0).getTime();
    if (a > b || (a === b && Number(ev.id || 0) > Number(cur.id || 0))) map[ev.invoice_ref] = ev;
  });
  return map;
}

// { [invoice_ref]: event } for invoices whose CURRENT state is removed.
export function currentRemovals(events) {
  const latest = latestRemovalByInvoice(events);
  const out = {};
  Object.keys(latest).forEach(k => { if (latest[k].action === "remove") out[k] = latest[k]; });
  return out;
}

// Guard used by insertOrderRemoval. `currentEvent` is the freshly fetched
// latest event for the invoice (or null); `invoiceStatus` is the freshly
// known invoice status. Returns null if allowed, otherwise an error string.
export function removalRefusal(action, currentEvent, invoiceStatus) {
  const isRemoved = !!currentEvent && currentEvent.action === "remove";
  if (action === "remove") {
    if (invoiceStatus !== "cancelled") return "Only a voided (cancelled) order can be removed. If it was voided on an older version of the portal, that void may not be saved to the database yet.";
    if (isRemoved) return "This order is already removed.";
    return null;
  }
  if (action === "restore") {
    if (!isRemoved) return "This order is not removed, nothing to restore.";
    return null;
  }
  return "Unknown action.";
}

// Forward-only server-state rule for SHARED_INVOICES.applyServerState.
// Only a DB status of 'cancelled' may be applied, and only when the cached
// invoice is not already cancelled. Everything else (paid / pending /
// payment_pending, shipping, tracking, notes, items, totals) is never touched
// because portal_orders.status is stale for payments.
export function serverCancelPatch(cached, row) {
  if (!cached || !row) return null;
  if (row.status !== "cancelled") return null;
  if (cached.status === "cancelled") return null;
  const patch = { status: "cancelled", shippingStatus: "cancelled" };
  if (row.cancel_reason) patch.cancelReason = row.cancel_reason;
  if (row.cancelled_date) {
    patch.cancelledAt = row.cancelled_date;
    const d = new Date(row.cancelled_date);
    if (!isNaN(d.getTime())) {
      patch.cancelledDate = d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
  }
  if (!patch.cancelReason) patch.cancelReason = "Cancelled by admin";
  return patch;
}

// Postgres 42P01 (undefined_table) or PostgREST PGRST205 (table not in the schema cache), or a
// message that names order_removals together with a "missing" phrase. The generic
// "could not query the database for the schema cache" text (transient PGRST002 outage) and any
// unrelated "does not exist" message must NOT count as a missing table.
export function isMissingTableError(err) {
  if (!err) return false;
  const code = String(err.code || "");
  if (code === "42P01" || code === "PGRST205") return true;
  const msg = String(err.message || err.details || err || "").toLowerCase();
  return msg.includes("order_removals") &&
    (msg.includes("does not exist") || msg.includes("could not find the table") || msg.includes("schema cache"));
}

// Reason validation for void. Note is required when reason is "Other".
export function validateVoidReason(reason, note) {
  const r = String(reason || "").trim();
  if (!r) return "Please choose a reason for voiding this order.";
  if (r === "Other" && !String(note || "").trim()) return "Please add a note explaining the reason (required when reason is Other).";
  return null;
}

// ── Shared admin-visibility selector (F5) ──────────────────────────────────
// ONE definition of "is this invoice hidden from admin views". removedMap is
// { [invoice_ref]: event } from currentRemovals().
export function isInvoiceRemoved(inv, removedMap) {
  return !!(inv && inv.id != null && removedMap && removedMap[inv.id]);
}

// Admin-visible invoices = every invoice that is not currently removed.
// Pure: returns a new array, never mutates the inputs.
export function visibleInvoices(invoices, removedMap) {
  const list = Array.isArray(invoices) ? invoices : [];
  if (!removedMap || Object.keys(removedMap).length === 0) return list.slice();
  return list.filter(inv => !isInvoiceRemoved(inv, removedMap));
}

// Merge freshly fetched removal events with the in-memory ones (F8). Events
// that arrived by realtime / local apply while a fetch was in flight are kept
// if the fetch result does not contain them (matched by id or client_id).
// Result is sorted by created_at then numeric id.
export function mergeRemovalEvents(fetched, inMemory) {
  const out = Array.isArray(fetched) ? fetched.filter(Boolean) : [];
  const seen = new Set();
  out.forEach(e => { if (e.id != null) seen.add("id:" + e.id); if (e.client_id) seen.add("cid:" + e.client_id); });
  (Array.isArray(inMemory) ? inMemory : []).forEach(e => {
    if (!e) return;
    if (e.id != null && seen.has("id:" + e.id)) return;
    if (e.client_id && seen.has("cid:" + e.client_id)) return;
    out.push(e);
    if (e.id != null) seen.add("id:" + e.id);
    if (e.client_id) seen.add("cid:" + e.client_id);
  });
  return out.sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    if (ta !== tb) return ta - tb;
    return Number(a.id || 0) - Number(b.id || 0);
  });
}

// Refund decision inputs captured BEFORE any await (F1): everything the
// refund + email logic needs, copied by value from the live cache object.
export function snapshotForRefund(inv) {
  const i = inv || {};
  return {
    id: i.id, orderId: i.orderId || null, status: i.status, paidMethod: i.paidMethod || null,
    paymentId: i.paymentId || null, transactionId: i.transactionId || null, paidLast4: i.paidLast4 || null,
    total: Number(i.total || 0), customer: i.customer || null,
  };
}

// Refund plan derived ONLY from a snapshot: "refund" | "manual" | "not_applicable".
export function refundPlan(snap) {
  const s = snap || {};
  const isPaidCardOrAch = s.status === "paid" && (s.paidMethod === "Credit card" || s.paidMethod === "ACH") && !!(s.paymentId || s.transactionId);
  if (isPaidCardOrAch) return "refund";
  if (s.status === "paid") return "manual";
  return "not_applicable";
}

// True when a cancelled invoice was paid by card/ACH yet has no refund record (F6).
export function cancelledWithoutRefundRecord(inv) {
  if (!inv || inv.status !== "cancelled") return false;
  const paidByCard = inv.paidMethod === "Credit card" || inv.paidMethod === "ACH" || !!inv.paymentId;
  return paidByCard && !inv.refundStatus;
}
